<script lang="ts">
  /**
   * Redigerer en planting i historikken: når den ble plantet og fjernet, og
   * notatet. Kan også slette den fra historikken (to trykk). Vises i et Sheet
   * fra historikken på kassa.
   */
  import { isoTilDato, datoTilIso, sjekkDatoer, lagrePlanting, slettFraHistorikk } from '../lib/plantinger';
  import { visFeil, visOk } from '../lib/toast';
  import type { PottePlanteFull } from '../lib/database.types';

  let {
    planting,
    onLukk,
    onEndret,
  }: { planting: PottePlanteFull; onLukk: () => void; onEndret: () => void } = $props();

  // Engangs-kopi ved åpning (arket remountes for hver planting).
  // svelte-ignore state_referenced_locally
  const p0 = planting;
  let plantet = $state(isoTilDato(p0.plantet_at));
  let fjernet = $state(p0.fjernet_at ? isoTilDato(p0.fjernet_at) : '');
  let notat = $state(p0.notater ?? '');
  let lagrer = $state(false);
  let bekreftSlett = $state(false);
  const idag = isoTilDato(new Date().toISOString());
  const feil = $derived(sjekkDatoer(plantet, fjernet));

  async function lagre() {
    if (feil || lagrer) return;
    lagrer = true;
    const ok = await lagrePlanting(p0.id, {
      plantet_at: datoTilIso(plantet, p0.plantet_at),
      fjernet_at: datoTilIso(fjernet, p0.fjernet_at),
      notater: notat.trim() || null,
    });
    lagrer = false;
    if (!ok) {
      visFeil('Kunne ikke lagre — sjekk nettet og prøv igjen.');
      return;
    }
    visOk('Lagret');
    onEndret();
    onLukk();
  }

  async function slett() {
    if (lagrer) return;
    lagrer = true;
    const ok = await slettFraHistorikk(p0.id);
    lagrer = false;
    if (!ok) {
      visFeil('Kunne ikke slette — sjekk nettet og prøv igjen.');
      return;
    }
    visOk('Slettet fra historikken');
    onEndret();
    onLukk();
  }
</script>

<div class="flex items-center gap-3">
  <span class="text-3xl leading-none">{p0.plante.emoji ?? '🌿'}</span>
  <div class="min-w-0">
    <div class="font-display text-[25px] font-semibold leading-[1.05] line-clamp-2 break-words">{p0.plante.navn}</div>
    <div class="font-mono text-[11px] text-text-muted mt-1">
      Historikk · Potte {Math.ceil(p0.seksjon / 2)}{p0.seksjon % 2 === 0 ? ' · bak' : ''}
    </div>
  </div>
</div>

<div class="flex flex-col gap-4 mt-5">
  <div class="grid grid-cols-2 gap-3">
    <div>
      <label for="plantet" class="label block mb-1.5">Plantet</label>
      <input id="plantet" type="date" bind:value={plantet} max={idag} class="input" />
    </div>
    <div>
      <label for="fjernet" class="label block mb-1.5">Fjernet</label>
      <input id="fjernet" type="date" bind:value={fjernet} min={plantet} max={idag} class="input" />
    </div>
  </div>
  {#if feil}
    <p class="text-[12px] text-rose -mt-2">{feil}</p>
  {/if}

  <div>
    <label for="hnotat" class="label block mb-1.5">Notat</label>
    <textarea id="hnotat" bind:value={notat} rows="2" placeholder="f.eks. «ga 12 g, bitter på slutten»" class="input resize-none"></textarea>
  </div>

  <button class="btn-primary w-full" onclick={lagre} disabled={lagrer || !!feil}>
    {lagrer ? 'Lagrer…' : 'Lagre'}
  </button>
  <button class="btn-secondary w-full" onclick={onLukk}>Avbryt</button>

  <div class="pt-2 border-t border-border">
    {#if bekreftSlett}
      <div class="text-[12px] text-rose mb-2 text-center">Slette {p0.plante.navn} fra historikken for godt?</div>
      <div class="flex gap-2.5">
        <button class="btn-secondary flex-1" onclick={() => (bekreftSlett = false)}>Nei</button>
        <button class="btn-danger flex-1" onclick={slett} disabled={lagrer}>Ja, slett</button>
      </div>
    {:else}
      <button class="text-[12px] text-rose hover:underline w-full text-center" onclick={() => (bekreftSlett = true)}>
        Slett fra historikken
      </button>
    {/if}
  </div>
</div>
